import React, { useState, useMemo, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Divider,
  ListItemIcon,
  Box,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import Logo from "assets/images/logos/DYKA Horizontal Logo.png";
import api from "lib/axios";
import { BRAND } from "assets/theme/brand";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import LogoStacked from "assets/images/logos/DYKA D Logo.png";

const SalesTopNavBar = () => {
  const { auth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleLogout = async () => {
    setAnchorEl(null);
    try {
      await api.post("/logout");
    } catch (_) {}
    try {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("role");
    } catch {}
    window.location.replace("/trade-login");
  };

  const menuItems = [
    { label: "Sales Overview", path: "/sales-overview", icon: <MonetizationOnIcon /> },
    { label: "UTM Marketing", path: "/marketing-utm", icon: <MonetizationOnIcon /> },
  ];

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log("[SalesTopNavBar] role?", {
      authRole: auth?.role,
      ls: localStorage.getItem("role"),
    });
  }, [auth?.role]);

  const flatMenu = useMemo(
    () => menuItems.map((item) => ({ label: item.label, path: item.path })),
    []
  );

  const getPageTitle = () => {
    const currentPath = location.pathname;
    const match = flatMenu.find((item) => currentPath.startsWith(item.path));
    return match ? match.label : "Sales Portal";
  };

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleNavigate = (path) => {
    handleClose();
    navigate(path);
  };

  const open = Boolean(anchorEl);
  const id = open ? "sales-nav-popover" : undefined;

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <>
      {/* Transparent floating top bar (logo left, title center, burger right) */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: "transparent",
          px: 2,
          py: 1,
          boxShadow: "none",
          backdropFilter: "blur(10px)",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", minHeight: "64px" }}>
          <Box
            component="img"
            src={isDesktop ? Logo : LogoStacked}
            alt="NetDefenders logo"
            sx={{
              height: { xs: 36, md: 50 }, // smaller on phones, larger on desktop
              width: "auto",
              flexShrink: 0,
            }}
          />

          <Typography
            variant="h3"
            sx={{
              flexGrow: 1,
              textAlign: "center",
              fontWeight: "bold",
              color: BRAND.cream, // same center title color
              userSelect: "none",
              position: "absolute",
              left: 0,
              right: 0,
              pointerEvents: "none",
            }}
          >
            {getPageTitle()}
          </Typography>

          <IconButton edge="end" onClick={handleMenuClick} sx={{ p: 1.5 }}>
            {/* Keep the lavender burger to match Admin/User; switch to BRAND.purple if preferred */}
            <MenuIcon sx={{ width: 44, height: 44, color: BRAND.orange }} />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Cream popover; orange section header; dark-blue items; purple icons */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            mt: 1,
            px: 1,
            py: 1,
            backgroundColor: BRAND.cream,
            color: BRAND.darkBlue,
            width: { xs: "60vw", sm: "260px" },
            boxShadow: "0px 6px 24px rgba(0,0,0,0.18)",
            overflowY: "auto",
            border: "1px solid rgba(0,0,0,0.06)",
          },
        }}
      >
        <List dense disablePadding>
          <ListSubheader
            disableSticky
            sx={{
              color: BRAND.orange, // orange and bold
              backgroundColor: "transparent",
              fontSize: "0.98rem",
              fontWeight: 800,
              px: 2,
              pt: 1.5,
            }}
          >
            Sales
          </ListSubheader>

          {menuItems.map((item) => (
            <ListItem
              key={item.label}
              onClick={() => handleNavigate(item.path)}
              sx={{
                borderRadius: 1.2,
                px: 2,
                py: 1.25,
                minHeight: 46,
                cursor: "pointer",
                "& .MuiListItemText-primary": {
                  fontSize: { xs: "1rem", sm: "0.92rem" },
                  fontWeight: 700,
                  color: BRAND.darkBlue, // page choices dark blue
                },
                "&:hover": { backgroundColor: "rgba(109,80,214,0.08)" }, // subtle purple hover
              }}
            >
              <ListItemIcon sx={{ color: BRAND.purple, minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItem>
          ))}

          <Divider sx={{ my: 1, borderColor: "rgba(0,0,0,0.08)" }} />

          <ListItem
            onClick={handleLogout}
            sx={{
              borderRadius: 1.2,
              px: 2,
              py: 1.25,
              minHeight: 46,
              cursor: "pointer",
              "& .MuiListItemText-primary": {
                fontSize: { xs: "1rem", sm: "0.92rem" },
                fontWeight: 700,
                color: "#c62828",
              },
              "&:hover": { backgroundColor: "rgba(198,40,40,0.08)" },
            }}
          >
            <ListItemIcon sx={{ color: "#c62828", minWidth: 36 }}>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItem>
        </List>
      </Popover>
    </>
  );
};

export default SalesTopNavBar;
